/**
 * One gallery image: summary fields visible, extra metadata behind “More details”.
 */
import React from "react";
import {
  PropertyFieldBinding,
  type FieldProps,
  type Property,
} from "@firecms/core";
import { ExpandablePanel, Typography } from "@firecms/ui";
import {
  IMAGE_DETAIL_KEYS,
  IMAGE_SUMMARY_KEYS,
} from "../lib/destinationImageFields";

function childProperties(
  property: FieldProps<Record<string, unknown>>["property"],
): Record<string, Property> {
  if (!("properties" in property) || !property.properties) return {};
  return property.properties as Record<string, Property>;
}

export function DestinationImageItemField({
  propertyKey,
  property,
  context,
  includeDescription,
  disabled,
  underlyingValueHasChanged,
  onPropertyChange,
}: FieldProps<Record<string, unknown>>) {
  const properties = childProperties(property);

  const renderKeys = (keys: readonly string[]) =>
    keys.map((key) => {
      const child = properties[key];
      if (!child) return null;
      return (
        <div key={key} className="w-full min-w-0">
          <PropertyFieldBinding
            propertyKey={`${propertyKey}.${key}`}
            property={child}
            context={context}
            includeDescription={includeDescription}
            disabled={disabled}
            underlyingValueHasChanged={underlyingValueHasChanged}
            partOfArray={false}
            minimalistView={false}
          />
        </div>
      );
    });

  return (
    <article className="flex flex-col gap-3 w-full min-w-0 rounded-md border border-surface-300 dark:border-surface-700 p-3">
      <div className="flex flex-col gap-2 w-full min-w-0">
        {renderKeys(IMAGE_SUMMARY_KEYS)}
      </div>
      <ExpandablePanel
        initiallyExpanded={property.expanded === true}
        onExpandedChange={(expanded) => onPropertyChange?.({ expanded })}
        title={
          <Typography variant="label" className="font-medium">
            More details
          </Typography>
        }
        innerClassName="px-2 md:px-4 pb-2 md:pb-4 pt-1 md:pt-2"
      >
        <div className="flex flex-col gap-2 w-full min-w-0">
          {renderKeys(IMAGE_DETAIL_KEYS)}
        </div>
      </ExpandablePanel>
    </article>
  );
}
